import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Drawer as MuiDrawer,
  Box,
  Divider,
  List,
  ListItemButton,
  IconButton,
  useTheme,
  Typography,
  ListItemText,
  Button,
} from "@mui/material";
import { darken } from "@mui/material/styles";
import { Menu, Language, Call, Handshake, Diversity3, ExpandLess, ExpandMore, InfoOutlined, ConfirmationNumber } from "@mui/icons-material";
import EggIcon from '@mui/icons-material/Egg';
import ArticleIcon from '@mui/icons-material/Article';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useTranslation } from "react-i18next";
import HomeIcon from "../../icons/header/HomeIcon";
import PromotionIcon from "../../icons/header/PromotionIcon";
import VipIcon from "../../icons/header/VipIcon";
import Image from "./Image";
import UserCard from "./UserCard";
import * as Constant from "../../features/constant";
import {
  useGetGameCategoryQuery,
} from "../../features/api/gameApiSlice";
import { useGetBlogListQuery } from "../../features/api/blogApiSlice";
import { selectCurrentToken } from "../../features/slices/authSlice";
import LanguageDropdown from "../common/LanguageDropdown";
import { Collapse, ListItemIcon } from "@mui/material";

const useImageWithFallback = (url) => {
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (!url) return;
    const img = new window.Image();
    img.src = url;
    img.onload = () => setIsValid(true);
    img.onerror = () => setIsValid(false);
  }, [url]);

  return isValid;
};

const drawerWidth = 220;

const Drawer = ({ user, window, isBD, selectedGameCategory, setSelectedGameCategory }) => {
  const token = useSelector(selectCurrentToken);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [openLang, setOpenLang] = useState(false);
  const toggleLang = () => setOpenLang((prev) => !prev);
  const bottomRef = React.useRef(null);
  const container =
    window !== undefined ? () => window().document.body : undefined;
  const [drawerOpen, setDrawerOpen] = useState(true); // Set initial state to true
  const isLoggedIn = useSelector(selectCurrentToken);

  // Load image URLs from env
  const promotionImgUrl = process.env.REACT_APP_COLOR_DRAWER_PROMOTIONICON_IMAGE;
  const vipImgUrl = process.env.REACT_APP_COLOR_DRAWER_VIPICON_IMAGE;

  // Check if they are valid
  const promotionImgValid = useImageWithFallback(promotionImgUrl);
  const vipImgValid = useImageWithFallback(vipImgUrl);

  const showReferral = process.env.REACT_APP_SHOW_REFERRAL === 'true';

  // Blog nav visibility: hide when the flag is off, and also hide when the flag
  // is on but there are no published posts. Same query args as DesktopHeader
  // so RTK Query dedupes to a single network request.
  const showBlogFlag = process.env.REACT_APP_SHOW_BLOG === 'true';
  const { data: blogProbe } = useGetBlogListQuery(
    { pageSize: 1 },
    { skip: !showBlogFlag },
  );
  const hasBlogs = (blogProbe?.result?.content?.length ?? 0) > 0;

  const drawerItems = [
    {
      label: t("drawer.home"),
      code: "home",
      icon: (
        <HomeIcon width={25} height={25} color={process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR || theme.palette.primary.main} />
      ),
    },
    ...(isLoggedIn && process.env.REACT_APP_GOLDEN_EGG ? [
      {
        label: t("drawer.goldenegg"),
        code: "goldenegg",
        icon: (
          <EggIcon sx={{ color: process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR, height: 25, width: 25 }} />
        ),
      },
    ] : []),

    ...(isLoggedIn && process.env.REACT_APP_SPINWHEEL ? [
      {
        label: t("drawer.spinwheel"),
        code: "SpinWheel",
        icon: (
          <EmojiEventsIcon sx={{ color: process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR, height: 25, width: 25 }} />
        ),
      },
    ] : []),

    ...(isLoggedIn ? [
      {
        label: t("drawer.dailymission"),
        code: "mission",
        icon: (
          <AssignmentIcon sx={{ color: process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR || theme.palette.primary.main, height: 25, width: 25 }} />
        ),
      },
    ] : []),

    ...(showReferral
      ? [
        {
          label: t("footer.affiliate"),
          code: "affiliate",
          icon: (
            <Handshake
              sx={{
                color:
                  process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR ||
                  theme.palette.primary.main,
              }}
            />
          ),
        },
      ]
      : []),
    {
      label: t("drawer.promotion"),
      code: "promotion",
      icon: promotionImgValid ? (
        <img
          src={promotionImgUrl}
          alt="promotion"
          width={25}
          height={25}
          style={{ objectFit: "contain" }}
        />
      ) : (
        <PromotionIcon
          width={25}
          height={25}
          color={
            process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR ||
            theme.palette.primary.main
          }
        />
      ),
    },
    // Gated on login: /redemptioncode sits inside RequireAuth, so showing this to a
    // logged-out visitor would just bounce them to the home page.
    ...(isLoggedIn
      ? [
        {
          label: t("drawer.redemptioncode"),
          // handleItemClick derives the route from the code, so this navigates to
          // /redemptioncode with no extra wiring.
          code: "redemptioncode",
          icon: (
            <ConfirmationNumber
              sx={{
                color:
                  process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR ||
                  theme.palette.primary.main,
                height: 25,
                width: 25,
              }}
            />
          ),
        },
      ]
      : []),
    {
      label: t("drawer.vip"),
      code: "vip",
      icon: vipImgValid ? (
        <img
          src={vipImgUrl}
          alt="vip"
          width={25}
          height={25}
          style={{ objectFit: "contain" }}
        />
      ) : (
        <VipIcon
          width={25}
          height={25}
          color={
            process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR ||
            theme.palette.primary.main
          }
        />
      ),
    },
  ];

  useEffect(() => {
    if (openLang && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [openLang]);

  const {
    data: gameCategory,
    isFetching: isGameCategoryFetching,
    isLoading: isGameCategoryLoading,
    isError: isGameCategoryError,
    isSuccess: isGameCategorySuccess,
    error: gameCategoryError,
  } = useGetGameCategoryQuery({ prefetch: true });

  const defaultCountry = process.env.REACT_APP_DEFAULT_LANGUAGE?.toUpperCase();
  const languageIcons = Constant.COUNTRY_LANGUAGE_INFO[defaultCountry]?.languages || [];

  Constant.getCustomMenuItemsV2(gameCategory?.result, t).map(item => {
    drawerItems.push({
      isGameCategory: true,
      label: t('gamecategory.' + item.code.toLowerCase().replaceAll("_", "").replaceAll("-", "")),
      code: item.code,
      icon: (
        <img src={item.imageDrawer || item.imageLogoOnly} alt={item.name} style={{ width: '25px', height: '25px' }} />
      ),
    });
  });

  if (process.env.REACT_APP_SHOW_REFERRAL === 'true') {
    drawerItems.push({
      label: t("drawer.referandearn"),
      code: "referral",
      icon: (
        <Diversity3
          sx={{
            color: process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR || theme.palette.primary.main,
          }}
        />
      ),
    });
  }
  if (showBlogFlag && hasBlogs) {
    drawerItems.push({
      label: t("drawer.blog"),
      code: "blog",
      icon: (
        <ArticleIcon
          sx={{
            color: process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR || theme.palette.primary.main,
          }}
        />
      ),
    });
  }
  drawerItems.push({
    label: t("drawer.contactus"),
    code: "contactus",
    icon: (
      <Call
        sx={{
          color: process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR || theme.palette.primary.main,
        }}
      />
    ),
  });
  drawerItems.push({
    label: t("drawer.infocenter"),
    code: "infocenter",
    icon: (
      <InfoOutlined
        sx={{
          color: process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR || theme.palette.primary.main,
        }}
      />
    ),
  });
  // {
  //   label: "Affiliate",
  //   icon: <Diversity3 sx={{ display: "block", width: "100%", mb: 1 }} />,
  // },
  // ];

  // const handleGameDrawerToggle = () => {
  //   setGameDrawerOpen(!gameDrawerOpen);
  // };

  const gridCodes = new Set(["home", "goldenegg", "SpinWheel", "mission"]);
  const regularItems = drawerItems.filter((item) => !item.isGameCategory && !gridCodes.has(item.code));
  const homeItem = drawerItems.filter((item) => item.code === "home");
  const extraGridItems = drawerItems.filter((item) => item.code === "goldenegg" || item.code === "SpinWheel" || item.code === "mission");
  const gameCategoryItems = [
    ...homeItem,
    ...drawerItems.filter((item) => item.isGameCategory),
    ...extraGridItems,
  ];

  const handleItemClick = (item) => {
    if (item.code === 'affiliate' && process.env.REACT_APP_AFFILIATE_SITE) {
      const a = document.createElement('a');
      a.href = process.env.REACT_APP_AFFILIATE_SITE;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      navigate(
        item?.code?.toLowerCase() === "home"
          ? Constant.getPath("/")
          : Constant.getPath("/" + item?.code?.toLowerCase().replace(/\s+/g, "")),
        item?.code?.toLowerCase() === "mission"
          ? { state: { userId: user?.userId } }
          // The standalone /redemptioncode route needs isNotProfileWallet=true so its
          // content box gets the top margin that clears the fixed MobileHeader. The
          // wallet-card entry (ProfileWallet.js) already passes this; without it the
          // page title renders under the fixed header and can't be scrolled into view.
          : item?.code?.toLowerCase() === "redemptioncode"
            ? { state: { isNotProfileWallet: true } }
            : item.isGameCategory
              ? { state: { scrollToCategory: item.code } }
              : undefined
      );
      if (item.isGameCategory) {
        setSelectedGameCategory(item.code);
      }
      setDrawerOpen(false);
    }
  };

  const drawerContent = (
    <>
      {!token ? (
        <Box sx={{ p: 2, textAlign: "center" }}>
          {/* Register button */}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => navigate({
              pathname: location.pathname,
              search: "?register=1",
            })}
            sx={{ mb: 1, fontWeight: "bold", fontSize: "1.1rem" }}
          >
            {t("home.signup")}
          </Button>

          {/* Already have account? Login */}
          <Typography
            variant="body2"
            fontSize="12px"
            color={process.env.REACT_APP_COLOR_TEXT || 'white'}
          >
            {t("drawer.haveaccount") + " "}
            <Typography
              component="span"
              sx={{
                color: process.env.REACT_APP_COLOR_TEXT || 'white',
                cursor: "pointer",
                textDecoration: "underline",
                fontSize: "12px",
              }}
              onClick={() => navigate({
                pathname: location.pathname,
                search: "?login=1",
              })}
            >
              {t("home.login")}
            </Typography>
          </Typography>
        </Box>
      ) : (
        <UserCard
          isDrawer={true}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          user={user}
        />
      )}
      <Divider
        sx={{ bgcolor: theme.palette.primary.main, height: "5px" }}
      />
      {/* Game category grid */}
      {gameCategoryItems.length > 0 && (
        <Box sx={{ px: 1, pt: 1, pb: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1,
            }}
          >
            {gameCategoryItems.map((item, index) => (
              <Box
                key={index}
                onClick={() => handleItemClick(item)}
                sx={{
                  height: 60,
                  borderRadius: 1,
                  bgcolor: darken(theme.palette.background.paper, 0.2),
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  p: "6px",
                  "&:active": {
                    bgcolor: darken(theme.palette.background.paper, 0.35),
                  },
                }}
              >
                <Box sx={{ "& img": { height: "25px !important", width: "auto !important" } }}>
                  {item.icon}
                </Box>
                <Typography
                  sx={{
                    fontSize: "9px",
                    fontWeight: 400,
                    color: process.env.REACT_APP_COLOR_TEXT || 'white',
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                    textAlign: "left",
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <List
        sx={{
          justifyContent: "center",
          alignSelf: "center",
          px: 3,
          paddingLeft: "0px"
        }}
      >
        {regularItems.map((item, index) => (
          <ListItemButton
            key={index}
            onClick={() => handleItemClick(item)}
            sx={{ width: "100%", mb: 0 }}
            disableRipple
          >
            <>
              {item.icon}
              <ListItemText
                sx={{
                  ml: 1,
                  fontWeight: 600,
                  lineHeight: 0,
                  fontSize: "14px",
                  color: process.env.REACT_APP_COLOR_TEXT || 'white',
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                }}
                disableTypography
              >
                {item.label}
              </ListItemText>
            </>
          </ListItemButton>
        ))}
      </List>
    </>
  );

  return (
    <>
      <MuiDrawer
        container={container}
        variant="temporary"
        open={drawerOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            setDrawerOpen(false);
          }
        }}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            bgcolor: theme.palette.background.paper,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Scrollable content */}
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {drawerContent}
        </Box>

        {/* Pinned language bottom section */}
        <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, p: 1, pb: 0, backgroundColor: darken(theme.palette.background.paper, 0.2) }}>
          <ListItemButton
            onClick={(event) => {
              event.stopPropagation();
              toggleLang();
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Language
                sx={{
                  color:
                    process.env.REACT_APP_COLOR_DRAWER_ICONCOLOR ||
                    theme.palette.primary.main,
                }}
              />
            </ListItemIcon>

            <ListItemText
              sx={{
                fontWeight: 600,
                fontSize: "14px",
                color: process.env.REACT_APP_COLOR_TEXT || 'white',
              }}
              disableTypography
            >
              {t("drawer.language")}
            </ListItemText>

            {openLang ? (
              <ExpandLess sx={{ color: process.env.REACT_APP_COLOR_TEXT || 'white' }} />
            ) : (
              <ExpandMore sx={{ color: process.env.REACT_APP_COLOR_TEXT || 'white' }} />
            )}
          </ListItemButton>

          {/* Collapsible submenu */}
          <Collapse in={openLang} timeout="auto" unmountOnExit sx={{ mx: -1 }}>
            <Box sx={{ pl: 0, pt: 1, backgroundColor: darken(theme.palette.background.paper, 0.4) }}>
              <LanguageDropdown languages={languageIcons} />
              <div ref={bottomRef} />
            </Box>
          </Collapse>
        </Box>
      </MuiDrawer>
    </>
  );
};

Drawer.propTypes = {
  user: PropTypes.object,
};

Drawer.defaultProps = {
  user: {},
};

export default Drawer;
